import { PageContainer } from "@/components/layout/PageContainer";
import { Helmet } from "react-helmet";
import { Contact } from "@/components/Contact";
import { ArrowRight, Calendar, Phone, Mail } from "lucide-react";

const ContactRequestDemo = () => {
  return (
    <PageContainer>
      <Helmet>
        <title>Contact & Request Demo - Scienza Health</title>
        <meta name="description" content="Schedule a personalized demo of our AI-powered healthcare platform and discover how it can transform your organization." />
      </Helmet>
      
      <div className="min-h-screen py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent">
              Schedule Your Demo
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Experience the power of AI-driven healthcare transformation. Schedule a personalized demo to see how our platform can revolutionize your patient care and operational efficiency.
            </p>
          </div>

          {/* Demo Benefits */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-center mb-8">What You'll Discover</h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-card border rounded-lg p-6 text-center">
                <Calendar className="h-12 w-12 text-primary mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-3">Live Platform Demo</h3>
                <p className="text-muted-foreground">
                  See our AI-powered healthcare platform in action with real-world scenarios tailored to your organization.
                </p>
              </div>
              <div className="bg-card border rounded-lg p-6 text-center">
                <Phone className="h-12 w-12 text-primary mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-3">Custom Use Cases</h3>
                <p className="text-muted-foreground">
                  Explore specific use cases and solutions designed for your healthcare delivery model and patient population.
                </p>
              </div>
              <div className="bg-card border rounded-lg p-6 text-center">
                <Mail className="h-12 w-12 text-primary mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-3">Implementation Roadmap</h3>
                <p className="text-muted-foreground">
                  Receive a customized implementation plan with timelines, milestones, and ROI projections for your organization.
                </p>
              </div>
            </div>
          </div>

          {/* Demo Options */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-center mb-8">Demo Options</h2>
            <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              <div className="bg-card border rounded-lg p-8">
                <h3 className="text-2xl font-semibold mb-4">Virtual Demo</h3>
                <ul className="space-y-3 text-muted-foreground mb-6">
                  <li className="flex items-start">
                    <ArrowRight className="h-5 w-5 text-primary mr-2 mt-0.5 flex-shrink-0" />
                    30-minute interactive presentation
                  </li>
                  <li className="flex items-start">
                    <ArrowRight className="h-5 w-5 text-primary mr-2 mt-0.5 flex-shrink-0" />
                    Q&A session with our experts
                  </li>
                  <li className="flex items-start">
                    <ArrowRight className="h-5 w-5 text-primary mr-2 mt-0.5 flex-shrink-0" />
                    Customized to your organization's needs
                  </li>
                  <li className="flex items-start">
                    <ArrowRight className="h-5 w-5 text-primary mr-2 mt-0.5 flex-shrink-0" />
                    Available worldwide via video conference
                  </li>
                </ul>
                <p className="text-sm text-muted-foreground">Perfect for initial exploration and team presentations</p>
              </div>

              <div className="bg-card border rounded-lg p-8">
                <h3 className="text-2xl font-semibold mb-4">On-Site Demo</h3>
                <ul className="space-y-3 text-muted-foreground mb-6">
                  <li className="flex items-start">
                    <ArrowRight className="h-5 w-5 text-primary mr-2 mt-0.5 flex-shrink-0" />
                    In-depth 60-minute demonstration
                  </li>
                  <li className="flex items-start">
                    <ArrowRight className="h-5 w-5 text-primary mr-2 mt-0.5 flex-shrink-0" />
                    Hands-on experience with the platform
                  </li>
                  <li className="flex items-start">
                    <ArrowRight className="h-5 w-5 text-primary mr-2 mt-0.5 flex-shrink-0" />
                    Technical integration discussion
                  </li>
                  <li className="flex items-start">
                    <ArrowRight className="h-5 w-5 text-primary mr-2 mt-0.5 flex-shrink-0" />
                    Meet with your entire team
                  </li>
                </ul>
                <p className="text-sm text-muted-foreground">Ideal for serious evaluation and implementation planning</p>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <Contact />
        </div>
      </div>
    </PageContainer>
  );
};

export default ContactRequestDemo;